/**
 * Shared E2E chat-key crypto for wallet devices and magic-link guests.
 *
 * Key scheme:
 *  - v1 (legacy): key = SHA-256(static challenge + room address). The room
 *    address is public, so this only obfuscates. Sending with v1 is
 *    DISABLED — the key is kept only to decrypt old messages still on the
 *    wire/history during the transition.
 *  - v2: key = SHA-256("dwdata-chat-key-v2|" + EIP-191 signature of a
 *    deterministic message). Producing the signature requires the wallet's
 *    private key, so only wallet owners can derive the key. EIP-191
 *    personal_sign is deterministic (RFC 6979) in all mainstream wallets,
 *    so every device of the shared company wallet derives the same key.
 *  - Guests (no wallet, or a different ephemeral wallet) obtain the raw v2
 *    key from an online wallet device via an ECDH (P-256) wrapped key
 *    exchange relayed as call signals — the relay only ever sees wrapped
 *    ciphertext.
 *
 * Wire format: v2 payloads are prefixed with "v2:"; v1 payloads are the
 * bare base64 envelope older clients produced. New clients only ever send
 * v2 — v1 remains decrypt-only during the transition.
 */
export const CHAT_KEY_V2_PREFIX = 'v2:';
/** Deterministic EIP-191 message signed to derive the v2 chat key. */
export function chatKeySignMessage(room) {
    return (`Dwinity chat encryption key (v2)\n` +
        `Room: ${room.toLowerCase()}\n` +
        `This signature is used only on this device to derive the end-to-end chat encryption key. It never leaves your device and costs nothing.`);
}
export class ChatKeyUnavailableError extends Error {
    constructor(message = 'No v2 chat key available to decrypt this message') {
        super(message);
        this.name = 'ChatKeyUnavailableError';
    }
}
// ---------- small helpers ----------
export function bytesToBase64(bytes) {
    let binary = '';
    for (let i = 0; i < bytes.length; i++)
        binary += String.fromCharCode(bytes[i]);
    return btoa(binary);
}
export function base64ToBytes(b64) {
    const binary = atob(b64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++)
        bytes[i] = binary.charCodeAt(i);
    return bytes;
}
export async function importAesKey(raw) {
    return crypto.subtle.importKey('raw', raw, { name: 'AES-GCM' }, true, [
        'encrypt',
        'decrypt',
    ]);
}
// ---------- key derivation ----------
/** Legacy v1 key bytes: SHA-256 of the public static challenge. */
export async function deriveV1KeyBytes(room) {
    const challenge = `This static text will be used to derive encryption key for ${room.toLowerCase()}`;
    const hash = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(challenge));
    return new Uint8Array(hash);
}
/** v2 key bytes from the wallet's EIP-191 signature. */
export async function deriveV2KeyBytesFromSignature(signature) {
    const material = `dwdata-chat-key-v2|${signature.toLowerCase()}`;
    const hash = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(material));
    return new Uint8Array(hash);
}
// ---------- session cache (per-tab; avoids re-prompting on reload) ----------
function v2CacheKey(room) {
    return `dwdata_chat_key_v2_${room.toLowerCase()}`;
}
export function loadCachedV2KeyBytes(room) {
    try {
        const b64 = sessionStorage.getItem(v2CacheKey(room));
        if (!b64)
            return null;
        const bytes = base64ToBytes(b64);
        return bytes.length === 32 ? bytes : null;
    }
    catch {
        return null;
    }
}
export function storeCachedV2KeyBytes(room, bytes) {
    try {
        sessionStorage.setItem(v2CacheKey(room), bytesToBase64(bytes));
    }
    catch {
        // ignore quota/availability errors — key stays in memory
    }
}
// ---------- envelope ----------
async function aesGcmEncrypt(text, key) {
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const encrypted = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, new TextEncoder().encode(text));
    const combined = new Uint8Array(iv.length + new Uint8Array(encrypted).length);
    combined.set(iv, 0);
    combined.set(new Uint8Array(encrypted), iv.length);
    return bytesToBase64(combined);
}
async function aesGcmDecrypt(b64, key) {
    const combined = base64ToBytes(b64);
    const iv = combined.slice(0, 12);
    const decrypted = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, combined.slice(12));
    return new TextDecoder().decode(decrypted);
}
/**
 * Encrypt with the v2 key only. The legacy v1 fallback (key derivable from
 * the public room address) has been removed: sending without a confirmed
 * v2 key throws ChatKeyUnavailableError instead of downgrading security.
 */
export async function encryptChatPayload(text, v2Key) {
    if (!v2Key)
        throw new ChatKeyUnavailableError('No v2 chat key available to encrypt — sending with the legacy v1 key is disabled');
    return CHAT_KEY_V2_PREFIX + (await aesGcmEncrypt(text, v2Key));
}
/**
 * Decrypt a versioned payload. Throws ChatKeyUnavailableError for a v2
 * payload when no v2 key is held yet (e.g. a guest still waiting for the
 * key exchange).
 */
export async function decryptChatPayload(payload, v2Key, v1Key) {
    if (payload.startsWith(CHAT_KEY_V2_PREFIX)) {
        if (!v2Key)
            throw new ChatKeyUnavailableError();
        return aesGcmDecrypt(payload.slice(CHAT_KEY_V2_PREFIX.length), v2Key);
    }
    return aesGcmDecrypt(payload, v1Key);
}
// ---------- ECDH key exchange (guest <-> wallet device, via relay) ----------
export const CHAT_KEY_REQUEST_SUBTYPE = 'chat_key_request';
export const CHAT_KEY_RESPONSE_SUBTYPE = 'chat_key_response';
export async function generateEcdhPair() {
    const pair = await crypto.subtle.generateKey({ name: 'ECDH', namedCurve: 'P-256' }, false, ['deriveBits']);
    const publicJwk = await crypto.subtle.exportKey('jwk', pair.publicKey);
    return { privateKey: pair.privateKey, publicJwk };
}
async function deriveSharedAesKey(ownPrivate, peerPubJwk) {
    const peerKey = await crypto.subtle.importKey('jwk', peerPubJwk, { name: 'ECDH', namedCurve: 'P-256' }, false, []);
    const bits = await crypto.subtle.deriveBits({ name: 'ECDH', public: peerKey }, ownPrivate, 256);
    const hashed = await crypto.subtle.digest('SHA-256', bits);
    return crypto.subtle.importKey('raw', hashed, { name: 'AES-GCM' }, false, [
        'encrypt',
        'decrypt',
    ]);
}
/** Wallet-device side: wrap the raw v2 key for a guest's ECDH public key. */
export async function wrapKeyForPeer(v2KeyBytes, peerPubJwk) {
    const ephemeral = await generateEcdhPair();
    const aes = await deriveSharedAesKey(ephemeral.privateKey, peerPubJwk);
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const wrapped = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, aes, v2KeyBytes);
    return {
        pub: ephemeral.publicJwk,
        iv: bytesToBase64(iv),
        wrapped: bytesToBase64(new Uint8Array(wrapped)),
    };
}
/** Guest side: unwrap the raw v2 key using our ECDH private key. */
export async function unwrapKeyFromPeer(response, ownPair) {
    const aes = await deriveSharedAesKey(ownPair.privateKey, response.pub);
    const raw = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: base64ToBytes(response.iv) }, aes, base64ToBytes(response.wrapped));
    const bytes = new Uint8Array(raw);
    if (bytes.length !== 32)
        throw new Error('Unwrapped chat key has unexpected length');
    return bytes;
}
